/**
 * Request Deduplication Utility
 * Prevents duplicate simultaneous requests to the same endpoint
 *
 * Usage:
 * const data = await deduplicateRequest('unique-key', () => api.fetchData());
 */

// Cache for in-flight requests
const inflightRequests = new Map<string, Promise<any>>();

/**
 * Deduplicate requests by key
 * If a request with the same key is already in-flight, return the existing promise
 * Otherwise, execute the request function and cache the promise
 *
 * @param key - Unique identifier for the request (e.g., 'auth:getCurrentUser')
 * @param requestFn - Function that returns a promise for the actual request
 * @returns Promise with the request result
 */
export function deduplicateRequest<T>(
  key: string,
  requestFn: () => Promise<T>
): Promise<T> {
  // Return existing promise if request is already in-flight
  if (inflightRequests.has(key)) {
    console.log(`[RequestCache] Using cached in-flight request: ${key}`);
    return inflightRequests.get(key)!;
  }

  console.log(`[RequestCache] Creating new request: ${key}`);

  // Create new request and cache it
  const promise = requestFn()
    .finally(() => {
      // Clean up after request completes (success or failure)
      inflightRequests.delete(key);
      console.log(`[RequestCache] Request completed and removed from cache: ${key}`);
    });

  inflightRequests.set(key, promise);
  return promise;
}

/**
 * Clear all in-flight requests (useful for logout or hard refresh)
 */
export function clearRequestCache(): void {
  inflightRequests.clear();
  console.log('[RequestCache] All cached requests cleared');
}

/**
 * Check if a request is currently in-flight
 */
export function isRequestInFlight(key: string): boolean {
  return inflightRequests.has(key);
}

/**
 * Get the number of in-flight requests
 */
export function getInflightRequestCount(): number {
  return inflightRequests.size;
}
